import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCategories } from 'reduxStore/categories/selectors';
import { categoriesActions } from 'reduxStore/categories/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteCategoryModal = ({ children, categoryIDs = [], visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const canDeleteCategory = useManagePermittedRoles('deleteCategory');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// ✅ Redux state
	const categories = useSelector(selectCategories);
	const { loadingDeleteCategory: loading, deleteCategoryComplete } = useSelector((state) => state.Categories);

	// ✅ Local State
	const [internalVisible, internalSetVisible] = useState(false);
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : internalSetVisible;

	// ✅ Filter selected categories
	const filteredCategories = useMemo(
		() => categories.filter((c) => categoryIDs.includes(c.categoryID)),
		[categories, categoryIDs]
	);

	// ✅ Generate selected category names
	const selectedNames = useMemo(
		() => filteredCategories.map((cat) => cat.name || `#${cat.categoryID}`),
		[filteredCategories]
	);

	// ✅ Generate dynamic messages
	const generateDeleteTitle = () =>
		categoryIDs.length > 1
			? `Are you sure you want to delete these ${categoryIDs.length} categories?`
			: 'Are you sure you want to delete this category?';

	const generateDeleteButtonText = () => (categoryIDs.length > 1 ? 'Delete Categories' : 'Delete');
	const triggerTitle = !canDeleteCategory ? permissionDeniedMessage : 'Delete this Category';

	// ✅ Close modal
	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// ✅ Handle deletion
	const handleDelete = () => {
		for (const categoryID of categoryIDs) {
			dispatch(categoriesActions.deleteCategory({ categoryID }));
		}
		setTimeout(() => {
			handleCancel();
		}, 500);
	};

	// ✅ Close modal on successful deletion
	useEffect(() => {
		if (deleteCategoryComplete) handleCancel();
	}, [deleteCategoryComplete, handleCancel]);

	return (
		<>
			<Tooltip title={triggerTitle} placement='top'>
				<span onClick={(e) => canDeleteCategory && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<DeletedFilled color='currentColor' width={16} height={16} />}
							disabled={!canDeleteCategory}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button onClick={handleDelete} loading={loading} disabled={loading} type='primary' danger block>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{generateDeleteTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Categories</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
};

export default DeleteCategoryModal;
